import Quill, { type Range } from "quill";
import Delta from "quill-delta";
import "quill/dist/quill.core.css";
import { canv } from "@/canv/global";
import { TableWidget } from "@/elements/table/table";
import { t } from "@/i18n";
import { clamp } from "@/shared/lib/clamp";
import { showSnackbar } from "@/snackbar/snackbar";
import { MAX_TEXT_LENGTH, SNACKBAR_TIME } from "./constants";
import { QUILL_BLOCK_FORMATS, QUILL_FORMATS } from "./constantsV2";
import type {
    InputKind,
    LimitNoticeKind,
    QuillFormats,
    SelectionSummary,
    TextChangeMeta,
} from "./typesV2";
import {
    enforceDeltaColor,
    getBoundsNearest,
    getBoundsOrNull,
    getSafeBounds,
    isSameLine,
} from "./utilsV2";

type SelectionRect = { left: number; top: number; width: number; height: number };

export class QuillInstance {
    readonly quill: Quill;
    private cleanup: Array<() => void> = [];
    private isTextChanged = false;
    private lastSelection: Range | null = null;
    private lastSelectionKey: string | null = null;
    private pendingInputKind: InputKind | null = null;
    private lastLimitNoticeKind: LimitNoticeKind | null = null;
    private mouseDownPoint: { x: number; y: number } | null = null;
    private hadMouseDragSelection = false;
    private forcedColor: string | null = null;

    onKeyDownCapture?: (e: KeyboardEvent) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
    onBlur?: (e: FocusEvent) => void;
    onPasteApplied?: () => void;

    constructor(fieldEl: HTMLElement) {
        this.quill = new Quill(fieldEl, {
            theme: undefined,
            placeholder: "",
            modules: { toolbar: false, history: { delay: 200, maxStack: 100, userOnly: true } },
            formats: QUILL_FORMATS,
        });
    }

    getEditor() {
        return this.quill.root;
    }

    load(delta: Delta) {
        this.isTextChanged = false;
        this.lastSelection = null;
        this.quill.setContents(delta, "silent");
        this.quill.history.clear();
    }

    clear() {
        this.isTextChanged = false;
        this.lastSelection = null;
        this.forcedColor = null;
        this.quill.setText("", "silent");
        this.quill.history.clear();
    }

    getDelta(): Delta {
        return this.quill.getContents();
    }

    getQuill() {
        return this.quill;
    }

    setForcedColor(color: string | null) {
        this.forcedColor = color;

        if (!color) {
            return;
        }

        this.syncForcedColorDocument(color, "silent");
        this.primeForcedColorAtCursor();
    }

    private syncForcedColorDocument(color: string, source: "silent" | "user") {
        const current = this.quill.getContents();
        const next = enforceDeltaColor(current, color);

        if (JSON.stringify(current.ops ?? []) === JSON.stringify(next.ops ?? [])) {
            return;
        }

        const range = this.quill.getSelection() ?? this.lastSelection;
        const patch = current.diff(next);

        this.quill.updateContents(patch, source);

        if (!range) {
            return;
        }

        const maxIndex = Math.max(0, this.quill.getLength() - 1);
        const index = clamp(range.index, 0, maxIndex);
        const maxLength = Math.max(0, this.quill.getLength() - 1 - index);
        const length = Math.max(0, Math.min(range.length, maxLength));

        this.lastSelection = { index, length };
        this.quill.setSelection(index, length, "silent");
    }

    convertClipboardHtmlToDelta(html: string, text?: string): Delta {
        return this.quill.clipboard.convert({ html, text: text || "" });
    }

    getHTML(): string {
        return this.quill.root.innerHTML;
    }

    getIsTextChanged() {
        return this.isTextChanged;
    }

    getLength() {
        return this.quill.getLength();
    }

    focus() {
        this.quill.focus();
    }

    focusToEnd() {
        this.quill.focus();
        const endIndex = Math.max(0, this.quill.getLength() - 1);
        this.quill.setSelection(endIndex, 0, "api");
    }

    async copySelectionToClipboard(): Promise<void> {
        const selection = this.quill.getSelection(true);
        if (!selection || selection.length <= 0) {
            return;
        }

        const text = this.quill.getText(selection.index, selection.length);
        const html = this.quill.getSemanticHTML(selection.index, selection.length);

        if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        "text/html": new Blob([html], { type: "text/html" }),
                        "text/plain": new Blob([text], { type: "text/plain" }),
                    }),
                ]);
                return;
            } catch (error) {
                console.warn(
                    "Не удалось записать форматированный текст в буфер, используем plain text",
                    error,
                );
            }
        }

        try {
            await navigator.clipboard?.writeText?.(text);
        } catch (error) {
            console.warn("Не удалось записать plain text в буфер", error);
        }
    }

    async pasteFromClipboard(): Promise<void> {
        this.quill.focus();

        try {
            if (navigator.clipboard?.read) {
                const items = await navigator.clipboard.read();

                for (const item of items) {
                    if (item.types.includes("text/html")) {
                        const htmlBlob = await item.getType("text/html");
                        const html = await htmlBlob.text();
                        const plainText = item.types.includes("text/plain")
                            ? await (await item.getType("text/plain")).text()
                            : "";

                        const delta = this.convertClipboardHtmlToDelta(html, plainText);
                        this.replaceSelectionWithDelta(delta, "user", "paste");
                        return;
                    }

                    if (item.types.includes("text/plain")) {
                        const textBlob = await item.getType("text/plain");
                        const text = await textBlob.text();

                        this.replaceSelectionWithText(text, "user", "paste");
                        return;
                    }
                }
            }
        } catch {
            // Clipboard.read может быть недоступен или запрещен политикой браузера.
            // Пробуем fallback через readText.
        }

        try {
            if (navigator.clipboard?.readText) {
                const text = await navigator.clipboard.readText();
                if (text) {
                    this.replaceSelectionWithText(text, "user", "paste");
                    return;
                }
            }
        } catch {
            // readText может быть заблокирован (permissions/secure context).
        }
    }

    deleteSelectionOrBackward(): void {
        this.quill.focus();

        const selection = this.quill.getSelection(true);
        if (!selection) {
            return;
        }

        if (selection.length > 0) {
            this.quill.deleteText(selection.index, selection.length, "user");
            this.quill.setSelection(selection.index, 0, "silent");
            return;
        }

        if (selection.index <= 0) {
            return;
        }

        const prevIndex = selection.index - 1;
        this.quill.deleteText(prevIndex, 1, "user");
        this.quill.setSelection(prevIndex, 0, "silent");
    }

    focusAtPoint(x: number, y: number) {
        this.quill.focus();
        this.setSelectionFromPoint(x, y);
    }

    setSelectionFromPoint(clientX: number, clientY: number) {
        const editorRoot = this.quill.root as HTMLElement;
        const editorRect = editorRoot.getBoundingClientRect();

        // Клик вне редактора — не трогаем.
        if (
            clientX < editorRect.left ||
            clientX > editorRect.right ||
            clientY < editorRect.top ||
            clientY > editorRect.bottom
        ) {
            return;
        }

        // Координаты клика в локальной системе координат root.
        const localX = clientX - editorRect.left;
        const localY = clientY - editorRect.top;

        const maxIndex = Math.max(0, this.quill.getLength() - 1);

        if (maxIndex === 0) {
            this.quill.setSelection(0, 0, "api");
            return;
        }

        // Если клик ниже последней строки — ставим каретку в конец.
        const lastCharHit = getBoundsNearest(this.quill, maxIndex, 0, maxIndex);
        if (lastCharHit) {
            const lastCharBounds = lastCharHit.b;

            if (localY > lastCharBounds.top + lastCharBounds.height) {
                this.quill.setSelection(maxIndex, 0, "api");
                return;
            }
        }

        // 1) Вертикальный бинарный поиск: ищем символ, который "первым накрывает" localY
        let searchLeftIndex = 0;
        let searchRightIndex = maxIndex;

        while (searchLeftIndex < searchRightIndex) {
            const middleIndex = (searchLeftIndex + searchRightIndex) >> 1;

            const middleHit = getBoundsNearest(
                this.quill,
                middleIndex,
                searchLeftIndex,
                searchRightIndex,
            );

            if (!middleHit) {
                this.quill.setSelection(0, 0, "api");
                return;
            }

            const middleBounds = middleHit.b;

            if (middleBounds.top + middleBounds.height < localY) {
                searchLeftIndex = middleHit.i + 1;
            } else {
                searchRightIndex = middleHit.i;
            }
        }

        const pickedHitByY = getBoundsNearest(this.quill, searchLeftIndex, 0, maxIndex);
        if (!pickedHitByY) {
            return;
        }

        const pickedBoundsByY = pickedHitByY.b;
        const pickedIndexByY = pickedHitByY.i;

        // 2) Расширяемся до границ строки (lineStart..lineEnd), чтобы дальше искать по X
        let lineStartIndex = pickedIndexByY;
        while (lineStartIndex > 0) {
            const prevCharBounds = getBoundsOrNull(this.quill, lineStartIndex - 1);
            if (!prevCharBounds) {
                break;
            }

            if (!isSameLine(prevCharBounds, pickedBoundsByY)) {
                break;
            }

            lineStartIndex--;
        }

        let lineEndIndex = pickedIndexByY;
        while (lineEndIndex < maxIndex) {
            const nextCharBounds = getBoundsOrNull(this.quill, lineEndIndex + 1);
            if (!nextCharBounds) {
                break;
            }

            if (!isSameLine(nextCharBounds, pickedBoundsByY)) {
                break;
            }

            lineEndIndex++;
        }

        // 3) Горизонтальный бинарный поиск внутри строки - выбираем символ по X, сравнивая localX с центром символа.
        let horizontalLeftIndex = lineStartIndex;
        let horizontalRightIndex = lineEndIndex;

        while (horizontalLeftIndex < horizontalRightIndex) {
            const middleIndex = (horizontalLeftIndex + horizontalRightIndex) >> 1;

            const middleHit = getBoundsNearest(
                this.quill,
                middleIndex,
                horizontalLeftIndex,
                horizontalRightIndex,
            );

            if (!middleHit) {
                break;
            }

            const middleBounds = middleHit.b;
            const charCenterX = middleBounds.left + middleBounds.width / 2;

            if (localX > charCenterX) {
                horizontalLeftIndex = middleHit.i + 1;
            } else {
                horizontalRightIndex = middleHit.i;
            }
        }

        // Если клик правее правой границы найденного символа — значит мы "после символа".
        // Тогда логичнее поставить каретку в конец строки.
        const resolvedCharBounds = getBoundsOrNull(this.quill, horizontalLeftIndex);
        if (resolvedCharBounds) {
            if (localX > resolvedCharBounds.left + resolvedCharBounds.width) {
                horizontalLeftIndex = lineEndIndex;
            }
        }

        const finalIndex = clamp(horizontalLeftIndex, 0, maxIndex);
        this.quill.setSelection(finalIndex, 0, "api");
    }

    getSelection() {
        const currentSelection = this.quill.getSelection();

        if (currentSelection !== null) {
            this.lastSelection = { index: currentSelection.index, length: currentSelection.length };
            return currentSelection;
        }

        if (this.lastSelection !== null) {
            return { index: this.lastSelection.index, length: this.lastSelection.length };
        }

        return null;
    }

    selectAll() {
        const fullLength = Math.max(0, this.quill.getLength() - 1);

        if (fullLength <= 0) {
            return;
        }

        this.quill.setSelection(0, fullLength, "api");
    }

    setLastSelection() {
        if (this.lastSelection) {
            this.quill.setSelection(this.lastSelection, "api");
        }
    }

    applyFormatting(name: QuillFormats, value: string | boolean) {
        if (QUILL_BLOCK_FORMATS.includes(name)) {
            this.applyBlock(name, value);
            return;
        }

        this.applyInline(name, value);
    }

    applyFormattingToDocument(name: QuillFormats, value: string | boolean) {
        if (QUILL_BLOCK_FORMATS.includes(name)) {
            const len = Math.max(0, this.quill.getLength() - 1);
            if (len > 0) {
                this.quill.formatLine(0, len, name, value, "user");
            } else {
                this.quill.format(name, value, "user");
            }
            return;
        }

        const len = Math.max(0, this.quill.getLength() - 1);

        if (name === "color" && typeof value === "string") {
            if (len > 0) {
                this.syncForcedColorDocument(value, "user");
            } else {
                this.quill.format("color", value, "user");
            }
            return;
        }

        if (len > 0) {
            this.quill.formatText(0, len, name, value, "user");
        } else {
            this.quill.format(name, value, "user");
        }
    }

    private applyInline(name: QuillFormats, value: string | boolean) {
        const range = name === "link" ? this.lastSelection : this.quill.getSelection();

        if (!range) {
            return;
        }

        if (name === "link") {
            this.applyLinkFormatting(range, value);
            return;
        }

        if (range.length === 0) {
            this.quill.format(name, value, "user");
            return;
        }

        this.quill.formatText(range.index, range.length, name, value, "user");
    }

    private applyLinkFormatting(range: Range, value: string | boolean) {
        if (range.length === 0) {
            this.quill.format("link", value, "user");

            if (value) {
                this.quill.format("color", false, "user");
            }

            return;
        }

        this.quill.formatText(range.index, range.length, "link", value, "user");

        if (value) {
            this.quill.formatText(range.index, range.length, "color", false, "user");
        }
    }

    private applyBlock(name: QuillFormats, value: string | boolean) {
        const range = this.quill.getSelection();

        if (!range) {
            return;
        }

        this.quill.formatLine(range.index, Math.max(1, range.length), name, value, "user");
    }

    private buildSelectionSummaryKey(summary) {
        return [
            `c:${summary.color ?? ""}`,
            `l:${summary.link ?? ""}`,
            `b:${summary.bold ?? ""}`,
            `i:${summary.italic ?? ""}`,
            `u:${summary.underline ?? ""}`,
            `s:${summary.strike ?? ""}`,
            `list:${summary.list ?? ""}`,
        ].join("|");
    }

    private setSelectionFromNativePoint(clientX: number, clientY: number): boolean {
        const doc = this.quill.root?.ownerDocument;

        if (!doc) {
            return false;
        }

        try {
            let nativeRange: globalThis.Range | null = null;

            if (typeof doc.caretPositionFromPoint === "function") {
                const caretPosition = doc.caretPositionFromPoint(clientX, clientY);
                const node = caretPosition?.offsetNode ?? null;
                const offset = caretPosition?.offset ?? null;

                if (node && offset !== null) {
                    nativeRange = doc.createRange();
                    nativeRange.setStart(node, offset);
                    nativeRange.collapse(true);
                }
            }

            if (!nativeRange && typeof doc.caretRangeFromPoint === "function") {
                nativeRange = doc.caretRangeFromPoint(clientX, clientY);
            }

            if (!nativeRange) {
                return false;
            }

            // Внутренний API Quill даёт точный индекс каретки для текущего DOM-range.
            const quillSelection = this.quill.selection;

            const normalized = quillSelection.normalizeNative(nativeRange);
            const quillRange = normalized ? quillSelection.normalizedToRange(normalized) : null;

            if (!quillRange) {
                return false;
            }

            const maxIndex = Math.max(0, this.quill.getLength() - 1);
            const index = clamp(quillRange.index, 0, maxIndex);
            this.quill.setSelection(index, 0, "api");
            return true;
        } catch {
            return false;
        }
    }

    onBeforeInput = (event: InputEvent) => {
        switch (event.inputType) {
            case "insertFromPaste":
                this.pendingInputKind = "paste-bulk";
                break;
            case "insertParagraph":
            case "insertLineBreak":
                this.pendingInputKind = "multiline-insert";
                break;
            case "insertText":
                this.pendingInputKind = "single-char-insert";
                break;
            case "deleteContentBackward":
            case "deleteContentForward":
            case "deleteByCut":
                this.pendingInputKind = "delete";
                break;
            default:
                this.pendingInputKind = null;
        }

        const available = this.getAvailableChars();

        switch (event.inputType) {
            case "insertText":
            case "insertParagraph":
            case "insertLineBreak": {
                if (available <= 0) {
                    event.preventDefault();
                    this.notifyAboutLimit("typing");
                }
                break;
            }

            case "insertFromPaste": {
                // Тут не режем, только не даём пройти, если места вообще нет.
                // Если место есть, paste обработаем ниже через Delta.
                if (available <= 0) {
                    event.preventDefault();
                    this.notifyAboutLimit("paste");
                }
                break;
            }
        }
    };

    onPaste = (event: ClipboardEvent) => {
        this.pendingInputKind = "paste-bulk";

        const clipboardData = event.clipboardData;

        if (!clipboardData) {
            return;
        }

        const html = clipboardData.getData("text/html");
        const text = clipboardData.getData("text/plain");

        if (!html && !text) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const available = this.getAvailableChars();

        if (available <= 0) {
            event.preventDefault();
            this.notifyAboutLimit("paste");
            return;
        }

        const delta = html
            ? this.convertClipboardHtmlToDelta(html, text)
            : new Delta().insert(text);

        this.replaceSelectionWithDelta(delta, "user", "paste");
        this.onPasteApplied?.();
    };

    onClick = (event: MouseEvent) => {
        if (this.hadMouseDragSelection) {
            this.hadMouseDragSelection = false;
            return;
        }

        // Не вмешиваемся в двойной/тройной клик и в клики не ЛКМ.
        if (event.detail !== 1 || event.button !== 0) {
            return;
        }
        // Не ломаем модифицированные сценарии выделения/навигации.
        if (event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) {
            return;
        }

        const selection = this.quill.getSelection();
        if (!selection || selection.length === 0) {
            return;
        }

        if (!this.setSelectionFromNativePoint(event.clientX, event.clientY)) {
            this.setSelectionFromPoint(event.clientX, event.clientY);
        }
    };

    onMouseDown = (event: MouseEvent) => {
        if (event.button !== 0) {
            this.mouseDownPoint = null;
            this.hadMouseDragSelection = false;
            return;
        }
        this.mouseDownPoint = { x: event.clientX, y: event.clientY };
        this.hadMouseDragSelection = false;
        canv.actionManager.startClickMove();
    };

    onMouseMove = (event: MouseEvent) => {
        if (!this.mouseDownPoint || (event.buttons & 1) === 0) {
            return;
        }

        const dx = Math.abs(event.clientX - this.mouseDownPoint.x);
        const dy = Math.abs(event.clientY - this.mouseDownPoint.y);

        if (dx > 2 || dy > 2) {
            this.hadMouseDragSelection = true;
        }
    };

    onMouseUp = () => {
        this.mouseDownPoint = null;
        canv.actionManager.stopClickMove();
    };

    attachListeners(
        handleTextChange?: (delta: Delta, meta: TextChangeMeta) => void,
        handleSelectionChange?: (summary: SelectionSummary) => void,
    ) {
        const editor = this.getEditor();
        // необходимо сбрасывать клики, чтобы правильно работал виджет использующие
        // проверку canv.actionManager.isMouseDown(), иначе будет залипане
        if (
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.selected[0]?.children?.[0] instanceof TableWidget
        ) {
            canv.actionManager.stopClickMove();
        }

        const onKeyDownCapture = (e: KeyboardEvent) => this.onKeyDownCapture?.(e);
        const onKeyDown = (e: KeyboardEvent) => this.onKeyDown?.(e);
        const onBlur = (e: FocusEvent) => this.onBlur?.(e);

        editor.addEventListener("keydown", onKeyDownCapture, true);
        editor.addEventListener("keydown", onKeyDown);
        editor.addEventListener("blur", onBlur);
        editor.addEventListener("beforeinput", this.onBeforeInput);
        editor.addEventListener("paste", this.onPaste, true);
        editor.addEventListener("click", this.onClick);
        editor.addEventListener("mousedown", this.onMouseDown);
        editor.addEventListener("mousemove", this.onMouseMove);
        editor.addEventListener("mouseup", this.onMouseUp);

        this.cleanup.push(() => editor.removeEventListener("keydown", onKeyDownCapture, true));
        this.cleanup.push(() => editor.removeEventListener("keydown", onKeyDown));
        this.cleanup.push(() => editor.removeEventListener("blur", onBlur));
        this.cleanup.push(() => editor.removeEventListener("beforeinput", this.onBeforeInput));
        this.cleanup.push(() => editor.removeEventListener("paste", this.onPaste, true));
        this.cleanup.push(() => editor.removeEventListener("click", this.onClick));
        this.cleanup.push(() => editor.removeEventListener("mousedown", this.onMouseDown));
        this.cleanup.push(() => editor.removeEventListener("mousemove", this.onMouseMove));
        this.cleanup.push(() => editor.removeEventListener("mouseup", this.onMouseUp));

        const onTextChange = (delta: Delta, oldDelta: Delta, source: string) => {
            if (source === "user") {
                this.isTextChanged = true;
            }

            const contentLength = Math.max(0, this.quill.getLength() - 1);

            if (source === "user" && contentLength > MAX_TEXT_LENGTH) {
                const currentSelection = this.quill.getSelection() ??
                    this.lastSelection ?? { index: 0, length: 0 };
                const limitKind: LimitNoticeKind =
                    this.pendingInputKind === "paste-bulk" ? "paste" : "typing";

                this.quill.setContents(oldDelta, "silent");

                const safeIndex = Math.min(
                    currentSelection.index,
                    Math.max(0, this.quill.getLength() - 1),
                );

                this.quill.setSelection(safeIndex, 0, "silent");
                this.lastSelection = { index: safeIndex, length: 0 };
                this.pendingInputKind = null;
                this.notifyAboutLimit(limitKind);
                return;
            }

            if (source === "user" && this.forcedColor) {
                this.primeForcedColorAtCursor();
            }

            if (!handleTextChange) {
                return;
            }

            if (source !== "user") {
                handleTextChange(delta, {
                    source,
                    inputKind: "unknown",
                    insertedTextLength: 0,
                    deletedLength: 0,
                    hasNewline: false,
                });
                return;
            }

            const meta = this.classifyDelta(delta);
            handleTextChange(delta, meta);
        };

        const onSelectionChange = (
            range: Range | null,
            _oldRange: Range | null,
            _source: string,
        ) => {
            if (range === null) {
                return;
            }

            this.lastSelection = { index: range.index, length: range.length };

            if (this.forcedColor && range.length === 0) {
                this.primeForcedColorAtCursor();
            }

            if (!handleSelectionChange) {
                return;
            }

            const format = this.quill.getFormat(range.index, range.length);

            const summary: SelectionSummary = {
                color: (format.color as string | undefined) ?? null,
                link: (format.link as string | undefined) ?? null,

                bold: typeof format.bold === "boolean" ? format.bold : null,
                italic: typeof format.italic === "boolean" ? format.italic : null,
                underline: typeof format.underline === "boolean" ? format.underline : null,
                strike: typeof format.strike === "boolean" ? format.strike : null,

                list: (format.list as any) ?? null,
            };

            const nextKey = this.buildSelectionSummaryKey(summary);

            if (!this.lastSelectionKey) {
                this.lastSelectionKey = nextKey;

                return;
            }

            if (nextKey === this.lastSelectionKey) {
                return;
            }

            this.lastSelectionKey = nextKey;
            handleSelectionChange(summary);
        };

        this.quill.on("text-change", onTextChange);
        this.quill.on("selection-change", onSelectionChange);
        this.cleanup.push(() => this.quill.off("text-change", onTextChange));
        this.cleanup.push(() => this.quill.off("selection-change", onSelectionChange));
    }

    detachListeners() {
        for (const fn of this.cleanup) {
            fn();
        }
        this.cleanup = [];
    }

    getSelectionRects(range: Range, scale: number): SelectionRect[] {
        if (range.length <= 0) {
            return [];
        }

        const container = this.quill.container as HTMLElement;
        const containerRect = container.getBoundingClientRect();

        // Пытаемся взять живой DOM Range, пока selection еще существует.
        const nativeSelection = document.getSelection();
        const nativeRange =
            nativeSelection && nativeSelection.rangeCount > 0
                ? nativeSelection.getRangeAt(0)
                : null;

        const domRects =
            nativeRange && !nativeRange.collapsed ? Array.from(nativeRange.getClientRects()) : [];

        const normalizedDomRects = domRects
            .filter((rect) => rect.width > 0 && rect.height > 0)
            .map((rect) => {
                const left = rect.left - containerRect.left;
                const top = rect.top - containerRect.top;

                return {
                    left: left / scale,
                    top: top / scale,
                    width: rect.width / scale,
                    height: rect.height / scale,
                };
            });

        if (normalizedDomRects.length > 0) {
            return this.mergeAdjacentRects(normalizedDomRects);
        }

        // Fallback на Quill bounds, но без fake-vertical-centering.
        const start = getSafeBounds(this.quill, range.index, 0);
        const end = getSafeBounds(this.quill, range.index + range.length, 0);

        if (!start || !end) {
            return [];
        }

        return [
            {
                left: start.left / scale,
                top: start.top / scale,
                width: Math.max(1, end.left - start.left) / scale,
                height: start.height / scale,
            },
        ];
    }

    mergeAdjacentRects(rects: SelectionRect[]): SelectionRect[] {
        const result: SelectionRect[] = [];

        for (const rect of rects) {
            const prev = result[result.length - 1];

            if (
                prev &&
                Math.abs(prev.top - rect.top) < 1 &&
                Math.abs(prev.height - rect.height) < 1 &&
                Math.abs(prev.left + prev.width - rect.left) < 2
            ) {
                prev.width = rect.left + rect.width - prev.left;
                continue;
            }

            result.push({ ...rect });
        }

        return result;
    }

    private classifyDelta(delta: Delta): TextChangeMeta {
        let insertedTextLength = 0;
        let deletedLength = 0;
        let hasNewline = false;

        for (const op of delta.ops ?? []) {
            if (typeof op.insert === "string") {
                insertedTextLength += op.insert.length;

                if (op.insert.includes("\n")) {
                    hasNewline = true;
                }
            } else if (op.insert != null) {
                insertedTextLength += 1;
            }

            if (typeof op.delete === "number") {
                deletedLength += op.delete;
            }
        }

        let inputKind: InputKind = this.pendingInputKind ?? "unknown";

        if (inputKind === "single-char-insert" && insertedTextLength !== 1) {
            inputKind = "unknown";
        }

        if (inputKind === "delete" && deletedLength > 1) {
            inputKind = "unknown";
        }

        if (inputKind === "multiline-insert" && !hasNewline) {
            inputKind = "unknown";
        }

        if (inputKind === "paste-bulk" && insertedTextLength <= 1 && !hasNewline) {
            inputKind = "unknown";
        }

        if (inputKind === "unknown") {
            const isInsert = insertedTextLength > 0;
            const isDelete = deletedLength > 0;

            if (hasNewline) {
                inputKind = "multiline-insert";
            } else if (isInsert && isDelete) {
                // replace (не важно bulk или нет)
                inputKind =
                    insertedTextLength > 3 || deletedLength > 3 ? "replace-bulk" : "replace";
            } else if (isInsert) {
                inputKind =
                    insertedTextLength === 1
                        ? "single-char-insert"
                        : insertedTextLength > 5
                          ? "paste-bulk"
                          : "multi-char-insert";
            } else if (isDelete) {
                inputKind = deletedLength > 1 ? "delete-bulk" : "delete";
            } else {
                inputKind = "format-only";
            }
        }

        this.pendingInputKind = null;

        return {
            source: "user",
            inputKind,
            insertedTextLength,
            deletedLength,
            hasNewline,
        };
    }

    private notifyAboutLimit(kind: LimitNoticeKind) {
        if (this.lastLimitNoticeKind === kind) {
            return;
        }

        this.lastLimitNoticeKind = kind;

        if (kind === "paste") {
            showSnackbar(t("text.limitPaste"), {
                duration: SNACKBAR_TIME,
                type: "warning",
                id: "limit-text-paste",
            });
        } else {
            showSnackbar(t("text.limitTyping"), {
                duration: SNACKBAR_TIME,
                type: "warning",
                id: "limit-text-typing",
            });
        }

        queueMicrotask(() => {
            this.lastLimitNoticeKind = null;
        });
    }

    private getSafeSelection(): Range {
        return (
            this.quill.getSelection(true) ?? {
                index: Math.max(0, this.quill.getLength() - 1),
                length: 0,
            }
        );
    }

    private getDeltaLength(delta: Delta): number {
        return (delta.ops ?? []).reduce((acc, op) => {
            if (typeof op.insert === "string") {
                return acc + op.insert.length;
            }

            if (op.insert != null) {
                return acc + 1;
            }

            return acc;
        }, 0);
    }

    private getAvailableChars(range = this.getSafeSelection()): number {
        const currentLength = Math.max(0, this.quill.getLength() - 1);

        // replace должен освобождать место на длину выделения
        return Math.max(0, MAX_TEXT_LENGTH - (currentLength - range.length));
    }

    private trimDeltaToLength(delta: Delta, maxLength: number): Delta {
        if (maxLength <= 0) {
            return new Delta();
        }

        let rest = maxLength;
        const out = new Delta();

        for (const op of delta.ops ?? []) {
            if (typeof op.insert !== "string") {
                continue;
            }

            if (rest <= 0) {
                break;
            }

            const chunk = op.insert.slice(0, rest);

            if (chunk.length > 0) {
                out.insert(chunk, op.attributes);
                rest -= chunk.length;
            }
        }

        return out;
    }

    replaceSelectionWithText(
        text: string,
        source: "user" | "api" = "user",
        limitKind: LimitNoticeKind = "typing",
    ) {
        if (!text) {
            return;
        }

        const delta = new Delta().insert(text);

        this.replaceSelectionWithDelta(delta, source, limitKind);
    }

    replaceSelectionWithDelta(
        delta: Delta,
        source: "user" | "api" = "user",
        limitKind: LimitNoticeKind = "typing",
    ) {
        const range = this.getSafeSelection();
        const available = this.getAvailableChars(range);

        const incomingLength = this.getDeltaLength(delta);

        if (incomingLength <= 0) {
            return false;
        }

        let deltaToInsert = delta;
        let wasTrimmed = false;

        if (incomingLength > available) {
            deltaToInsert = this.trimDeltaToLength(delta, available);
            wasTrimmed = true;
        }

        const acceptedLength = this.getDeltaLength(deltaToInsert);

        if (acceptedLength <= 0) {
            this.notifyAboutLimit(limitKind);
            return;
        }

        const change = new Delta().retain(range.index).delete(range.length).concat(deltaToInsert);

        this.quill.updateContents(change, source);
        this.quill.setSelection(range.index + acceptedLength, 0, "silent");

        if (wasTrimmed) {
            this.notifyAboutLimit(limitKind);
        }
    }

    private primeForcedColorAtCursor() {
        const color = this.forcedColor;

        if (!color) {
            return;
        }

        const range = this.quill.getSelection() ?? this.lastSelection;

        if (!range || range.length > 0) {
            return;
        }

        const maxIndex = Math.max(0, this.quill.getLength() - 1);
        const index = clamp(range.index, 0, maxIndex);
        const format = this.quill.getFormat(index, 0);

        if (format.link) {
            return;
        }

        if (format.color === color) {
            return;
        }

        this.quill.setSelection(index, 0, "silent");
        this.quill.format("color", color, "silent");
    }
}
